import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Search, Upload, Sparkles, Download, RefreshCw, RotateCcw, ArrowLeft, ImagePlus, X, Wand2, Check, Instagram, ClipboardPaste } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { readClipboardImageFile, imageFileFromPasteEvent, isSupportedImage } from "@/lib/clipboardImage";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const ASSIST_ACTIONS = [
  { k: "fix_grammar", l: "Fix Grammar" }, { k: "spell_check", l: "Spell Check" },
  { k: "shorter", l: "Make Shorter" }, { k: "crisp", l: "Make Crisp" },
  { k: "creative", l: "Make Creative" }, { k: "professional", l: "Make Professional" },
  { k: "rewrite", l: "Rewrite" }, { k: "headline", l: "Generate Headline" }, { k: "caption", l: "Generate Caption" },
];

// Recommended aspect per social platform (drives the prompt + aspect suggestion).
const PLATFORMS = [
  { k: "instagram_feed", l: "Instagram Feed", aspect: "4:5" },
  { k: "instagram_square", l: "Instagram Square", aspect: "1:1" },
  { k: "instagram_story", l: "Instagram Story / Reel", aspect: "9:16" },
  { k: "landscape", l: "Landscape / YouTube", aspect: "16:9" },
];

// Category-specific prompt enhancement. Unknown categories fall back to a strong generic line.
const CATEGORY_PROMPTS = {
  cricket: "Create a cinematic sports graphic with dynamic lighting, dramatic subject emphasis, energetic composition, premium sports typography, strong contrast and professional broadcast-style visual hierarchy.",
  sports: "Create a cinematic sports graphic with dynamic lighting, dramatic subject emphasis, energetic composition, premium sports typography, strong contrast and professional broadcast-style visual hierarchy.",
  football: "Use an energetic football/soccer visual language with stadium atmosphere, dramatic lighting, motion-inspired composition and bold sports typography.",
  soccer: "Use an energetic football/soccer visual language with stadium atmosphere, dramatic lighting, motion-inspired composition and bold sports typography.",
  news: "Use a clean editorial news composition with strong headline hierarchy, credible visual structure, readable typography and professional newsroom aesthetics.",
  meme: "Use a highly readable, humorous social-media composition with strong visual timing, expressive imagery and concise text.",
  memes: "Use a highly readable, humorous social-media composition with strong visual timing, expressive imagery and concise text.",
  motivational: "Use an inspirational premium composition with strong subject focus, dramatic lighting, elegant typography and emotionally engaging visual hierarchy.",
  motivation: "Use an inspirational premium composition with strong subject focus, dramatic lighting, elegant typography and emotionally engaging visual hierarchy.",
  gym: "Use an intense fitness aesthetic with dramatic lighting, muscular subject emphasis, energetic composition and bold athletic typography.",
  fitness: "Use an intense fitness aesthetic with dramatic lighting, muscular subject emphasis, energetic composition and bold athletic typography.",
  food: "Use appetizing food photography aesthetics, natural presentation, rich detail, premium composition and visually appealing typography.",
  science: "Use a modern scientific/technology aesthetic with clean composition, futuristic details, professional typography and visually accurate presentation.",
  education: "Use a clean educational design with clear information hierarchy, readable typography and professional visual organization.",
  tech: "Use a modern technology aesthetic with futuristic visual elements, clean geometry, premium lighting and strong digital typography.",
  technology: "Use a modern technology aesthetic with futuristic visual elements, clean geometry, premium lighting and strong digital typography.",
  politics: "Use an editorial political-news visual style with clear information hierarchy, strong subject presentation and professional news-oriented composition.",
};
const categorySnippet = (cat) => CATEGORY_PROMPTS[(cat || "").toLowerCase().trim()] ||
  "Use a premium, professional composition with strong visual hierarchy, balanced spacing, clear typography and polished lighting tailored to the topic.";

function buildPrompt({ category, aspect, platform, headline, text }) {
  const plat = PLATFORMS.find((p) => p.k === platform);
  const base =
    "Create a premium, high-quality social media graphic based on the supplied template/reference image. " +
    "Preserve the overall visual hierarchy, composition logic, spacing, typography hierarchy, subject positioning and professional design language of the reference while creating an original composition. " +
    "Use the supplied logo, main image, reference images, headline and information appropriately. " +
    "Do not copy unrelated copyrighted content or reproduce an existing thumbnail exactly. " +
    "Adapt the design specifically for the selected category and aspect ratio.";
  const lines = [base, ""];
  lines.push(`Category: ${category || "General"}`);
  lines.push(`Aspect Ratio: ${aspect}`);
  if (plat) lines.push(`Platform: ${plat.l}`);
  if (headline) lines.push(`Headline: ${headline}`);
  if (text) lines.push(`Information: ${text}`);
  lines.push("");
  lines.push("Use the supplied images as visual references and place the main subject prominently. " +
    "Maintain accurate spelling, names, numbers, scores, dates and factual information. Keep the user's language exactly (do not translate). " +
    "Make typography highly readable with strong visual hierarchy, professional spacing, balanced composition, appropriate contrast and premium lighting. " +
    "Generate a polished, publication-ready social media graphic.");
  lines.push("");
  lines.push(categorySnippet(category));
  lines.push(`Design specifically for a ${aspect} social media composition. Keep all important text and subjects inside safe margins.`);
  return lines.join("\n");
}

function AssistField({ label, testid, value, onChange, textarea, placeholder, ocr }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const ocrRef = useRef(null);
  const run = async (action) => {
    setBusy(action); setSuggestion(null);
    try { const { suggestion: s } = await api.igTextAssist(action, value); setSuggestion({ action, text: s }); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI Assist failed."); }
    setBusy(null);
  };
  const extractFrom = async (file) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    setOcrBusy(true);
    try {
      const { text } = await api.igOcr(file);
      if (!text || !text.trim()) { toast.error("No readable text found in the image."); }
      else { onChange(value && value.trim() ? `${value.trim()}\n${text.trim()}` : text.trim()); toast.success("Text extracted from image"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Text extraction failed."); }
    setOcrBusy(false);
  };
  const pasteOcr = async () => {
    try { const f = await readClipboardImageFile(); await extractFrom(f); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };
  const Field = textarea ? "textarea" : "input";
  return (
    <div data-testid={`igv2-field-${testid}`}>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-semibold text-[#A1A1AA]">{label}</label>
        <div className="flex items-center gap-1.5">
          {ocr && (
            <>
              <input ref={ocrRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`igv2-ocr-upload-${testid}`}
                onChange={(e) => { extractFrom(e.target.files?.[0]); e.target.value = ""; }} />
              <button type="button" onClick={pasteOcr} disabled={ocrBusy} data-testid={`igv2-ocr-paste-${testid}`}
                className="flex items-center gap-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-0.5 text-[10px] font-bold text-[#7fe9f2] hover:bg-[#00F0FF]/20 disabled:opacity-50">
                {ocrBusy ? <Loader2 size={11} className="animate-spin" /> : <ClipboardPaste size={11} />} Paste Image → Text</button>
              <button type="button" onClick={() => ocrRef.current?.click()} disabled={ocrBusy} data-testid={`igv2-ocr-btn-${testid}`}
                className="flex items-center gap-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-0.5 text-[10px] font-bold text-[#7fe9f2] hover:bg-[#00F0FF]/20 disabled:opacity-50"><Upload size={11} /> Image</button>
            </>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`igv2-assist-toggle-${testid}`}
            className="flex items-center gap-1 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-2 py-0.5 text-[10px] font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20"><Wand2 size={11} /> AI Assist</button>
        </div>
      </div>
      <Field value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`igv2-input-${testid}`}
        rows={textarea ? 4 : undefined}
        className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
      {open && (
        <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.03] p-2" data-testid={`igv2-assist-panel-${testid}`}>
          <div className="flex flex-wrap gap-1.5">
            {ASSIST_ACTIONS.map((a) => (
              <button key={a.k} type="button" onClick={() => run(a.k)} disabled={!!busy} data-testid={`igv2-assist-${a.k}-${testid}`}
                className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-40">
                {busy === a.k ? <Loader2 size={11} className="animate-spin" /> : a.l}
              </button>
            ))}
          </div>
          {suggestion && (
            <div className="mt-2 rounded-lg border border-[#8B5CF6]/30 bg-black/40 p-2" data-testid={`igv2-assist-suggestion-${testid}`}>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#a78bfa]">Suggestion</div>
              <div className="whitespace-pre-wrap text-sm text-white">{suggestion.text}</div>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => { onChange(suggestion.text); setSuggestion(null); toast.success("Applied"); }} data-testid={`igv2-assist-use-${testid}`}
                  className="flex items-center gap-1 rounded-lg bg-[#8B5CF6] px-2.5 py-1 text-[11px] font-bold text-white"><Check size={12} /> Use Suggestion</button>
                <button type="button" onClick={() => setSuggestion(null)} className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA]">Keep Original</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Single-image field with Upload + Paste + Replace/Remove — routes through the existing igUpload pipeline.
function ImageField({ label, testid, role, value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    setBusy(true);
    try { const res = await api.igUpload(file, role); onChange(res); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    setBusy(false);
  };
  const paste = async () => {
    try { const f = await readClipboardImageFile(); await send(f); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };
  return (
    <div data-testid={`igv2-imagefield-${testid}`}>
      <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">{label}</label>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`igv2-upload-${testid}`}
        onChange={(e) => { send(e.target.files?.[0]); e.target.value = ""; }} />
      {value ? (
        <div className="flex items-center gap-2">
          <img src={abs(value.url)} alt={label} className="h-16 w-16 rounded-lg border border-white/15 object-cover" data-testid={`igv2-preview-${testid}`} />
          <div className="flex flex-col gap-1">
            <button onClick={() => ref.current?.click()} data-testid={`igv2-replace-${testid}`} className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60">Replace</button>
            <button onClick={paste} data-testid={`igv2-paste-${testid}`} className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-white hover:border-[#8B5CF6]/60">Paste</button>
            <button onClick={() => onChange(null)} data-testid={`igv2-remove-${testid}`} className="rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-[#A1A1AA] hover:text-red-400">Remove</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`igv2-pick-${testid}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/20 py-3 text-xs font-semibold text-[#A1A1AA] hover:border-[#8B5CF6]/60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <><Upload size={13} /> Upload</>}
          </button>
          <button onClick={paste} data-testid={`igv2-pastebtn-${testid}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/20 py-3 text-xs font-semibold text-[#A1A1AA] hover:border-[#8B5CF6]/60">
            <ClipboardPaste size={13} /> Paste
          </button>
        </div>
      )}
    </div>
  );
}

const PROGRESS_STEPS = ["Preparing references", "Building composition", "Generating image", "Finalising design"];

export default function InstagramPostMakerV2({ preview = false }) {
  useFeatures();
  const [cfg, setCfg] = useState(null);
  const [stage, setStage] = useState("library"); // library | studio | generating | result
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(true);
  const [tpl, setTpl] = useState(null);

  const [logo, setLogo] = useState(null);
  const [main, setMain] = useState(null);
  const [refs, setRefs] = useState([]);
  const [headline, setHeadline] = useState("");
  const [text, setText] = useState("");
  const [category, setCategory] = useState("General");
  const [aspect, setAspect] = useState("1:1");
  const [platform, setPlatform] = useState("");
  const [extra, setExtra] = useState(""); // additional user direction

  // Prompt states: genPrompt (auto) vs promptText (editable). edited stops auto-overwrite.
  const [genPrompt, setGenPrompt] = useState("");
  const [promptText, setPromptText] = useState("");
  const [edited, setEdited] = useState(false);

  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [brandKit, setBrandKit] = useState({ logo_path: null, logo_url: null, colors: [] });

  useEffect(() => {
    api.igConfig().then((c) => { setCfg(c); if (c.aspects?.length) setAspect(c.aspects[0].key); }).catch(() => {});
    api.igGetBrandKit().then((k) => setBrandKit(k)).catch(() => {});
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoadingTpl(true);
    try {
      const [c, t] = await Promise.all([api.igCategories(), api.igTemplates(activeCat, q)]);
      setCats(c); setTemplates(t);
    } catch { /* feature may be off */ }
    setLoadingTpl(false);
  }, [activeCat, q]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Auto-build the suggested prompt whenever inputs change (unless the user has edited it).
  // Prefer the admin-managed, versioned prompt assembly; fall back to the local builder.
  useEffect(() => {
    let cancelled = false;
    const local = buildPrompt({ category, aspect, platform, headline, text });
    const t = setTimeout(async () => {
      let g = local;
      try {
        const r = await api.igAssemblePrompt({ category, output_type: "normal_post", aspect, platform: platform || null, headline, text });
        if (r?.prompt) g = r.prompt;
      } catch { /* fall back to local */ }
      if (cancelled) return;
      setGenPrompt(g);
      if (!edited) setPromptText(g);
    }, 350);
    // Show local immediately so the box is never empty.
    setGenPrompt(local);
    if (!edited) setPromptText(local);
    return () => { cancelled = true; clearTimeout(t); };
  }, [category, aspect, platform, headline, text, edited]);

  const chooseTemplate = (t) => {
    setTpl(t); setCategory(t.category || category); setAspect(t.recommended_aspect || aspect);
    if (!logo && brandKit.logo_path) setLogo({ path: brandKit.logo_path, url: brandKit.logo_url });
    setStage("studio");
  };
  const setReferenceTemplate = async (file, origin) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    try {
      const res = await api.igUpload(file, "template");
      if (!logo && brandKit.logo_path) setLogo({ path: brandKit.logo_path, url: brandKit.logo_url });
      setTpl({ id: "user_reference", name: origin === "clipboard" ? "Pasted from clipboard" : (file.name || "Your reference"), image_url: res.url, path: res.path, source: "user_reference", origin, recommended_aspect: aspect });
      setStage("studio");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };
  const pasteReference = async () => {
    try { const f = await readClipboardImageFile(); await setReferenceTemplate(f, "clipboard"); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };

  const addRef = async (file) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    try { const res = await api.igUpload(file, "reference"); setRefs((r) => [...r, res]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
  };
  const pasteRef = async () => {
    try { const f = await readClipboardImageFile(); await addRef(f); }
    catch (e) { toast.error(e?.message || "Nothing to paste."); }
  };

  const generate = async () => {
    if (!tpl) return;
    setStage("generating"); setProgress(0); setResult(null);
    const timer = setInterval(() => setProgress((p) => Math.min(p + 1, PROGRESS_STEPS.length - 1)), 2200);
    const overridePrompt = promptText + (extra.trim() ? `\n\nAdditional user direction:\n${extra.trim()}` : "");
    try {
      const res = await api.igGenerate({
        template_source: tpl.source === "user_reference" ? "user_reference" : "admin_template",
        template_id: tpl.source === "user_reference" ? null : tpl.id,
        template_path: tpl.source === "user_reference" ? tpl.path : null,
        aspect, headline, text, prompt: extra, override_prompt: overridePrompt,
        version: "v2", platform: platform || null,
        logo_path: logo?.path || null, main_path: main?.path || null,
        ref_paths: refs.map((r) => r.path),
        brand_colors: brandKit.colors && brandKit.colors.length ? brandKit.colors : undefined,
      });
      clearInterval(timer); setProgress(PROGRESS_STEPS.length);
      setResult(res);
      setHistory((h) => [{ ...res, template_name: tpl.name, at: new Date() }, ...h].slice(0, 8));
      setStage("result");
    } catch (e) {
      clearInterval(timer);
      toast.error(e?.response?.data?.detail || "Generation failed. Please try again.");
      setStage("studio");
    }
  };

  const download = async (url, generationId) => {
    try {
      const r = await fetch(abs(url), { credentials: "include" });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `instagram-post-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      if (generationId) api.igRegisterDownload(generationId).catch(() => {});
    } catch { toast.error("Download failed."); }
  };

  const startAgain = () => { setTpl(null); setMain(null); setRefs([]); setHeadline(""); setText(""); setExtra(""); setResult(null); setEdited(false); setStage("library"); };

  const enabled = cfg?.enabled;
  if (cfg && !enabled && !preview) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center" data-testid="igv2-unavailable">
          <Instagram size={40} className="mb-4 text-[#E1306C]" />
          <h1 className="font-display text-2xl font-black text-white">Instagram Post Maker is unavailable</h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">This feature is currently turned off. Please check back later.</p>
        </div>
      </div>
    );
  }

  const aspects = cfg?.aspects || [{ key: "1:1", w: 1080, h: 1080 }];
  const catOptions = ["General", ...cats.map((c) => c.name)];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      {preview && (
        <div className="bg-[#8B5CF6]/15 py-1.5 text-center text-xs font-bold text-[#c4b5fd]" data-testid="igv2-preview-banner">Admin preview — this version is not necessarily live for users</div>
      )}
      <main className="mx-auto max-w-[1200px] px-5 py-8" data-testid="instagram-post-maker-v2">
        {stage === "library" && (
          <>
            <div className="mb-6">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#E1306C]/40 bg-[#E1306C]/10 px-3 py-1 text-xs font-bold text-[#f48fb1]"><Instagram size={13} /> AI Creative Studio</div>
              <h1 className="font-display text-4xl font-black tracking-tight text-white sm:text-5xl">Instagram Post Maker</h1>
              <p className="mt-2 max-w-xl text-sm text-[#A1A1AA] sm:text-base">Pick a reference, add your content, and let AI compose a premium post. Every field supports Upload or Paste.</p>
            </div>

            <div className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="igv2-template-source">
              <div className="rounded-xl border border-dashed border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.04] p-5">
                <div className="mb-3 text-sm font-bold text-white">Reference Template</div>
                <label className="mb-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-3 py-2.5 text-sm font-bold text-[#a78bfa] hover:bg-[#8B5CF6]/20">
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="igv2-upload-reference-template"
                    onChange={(e) => { setReferenceTemplate(e.target.files?.[0], "upload"); e.target.value = ""; }} />
                  <Upload size={16} /> Upload Template
                </label>
                <button type="button" onClick={pasteReference} data-testid="igv2-paste-reference-template"
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2.5 text-sm font-bold text-white hover:border-[#8B5CF6]/60">
                  <ClipboardPaste size={16} /> Paste From Clipboard
                </button>
                <div className="mt-2 text-[11px] text-[#A1A1AA]">Use your own image as the AI reference (PNG / JPG / WEBP).</div>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
                <Instagram size={22} className="text-[#E1306C]" />
                <div className="text-sm font-bold text-white">Pick From Our Templates</div>
                <div className="text-xs text-[#A1A1AA]">Browse the ready-made template library below</div>
              </div>
            </div>

            <div className="mb-2 text-sm font-bold uppercase tracking-widest text-[#A1A1AA]">Our Templates</div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <Search size={15} className="text-[#666]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" data-testid="igv2-template-search"
                  className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#666]" />
              </div>
            </div>
            <div className="mb-5 flex flex-wrap gap-2" data-testid="igv2-category-filter">
              {["All", ...cats.map((c) => c.name)].map((c) => (
                <button key={c} onClick={() => setActiveCat(c)} data-testid={`igv2-cat-${c}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${activeCat === c ? "bg-[#E1306C] text-white" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{c}</button>
              ))}
            </div>
            {loadingTpl ? (
              <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E1306C]" /></div>
            ) : templates.length === 0 ? (
              <div className="py-20 text-center text-sm text-[#666]" data-testid="igv2-no-templates">No templates yet in this category.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="igv2-template-grid">
                {templates.map((t) => (
                  <div key={t.id} data-testid={`igv2-template-${t.id}`} className="group overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]">
                    <div className="aspect-square overflow-hidden bg-black">
                      <img src={abs(t.thumbnail_url || t.image_url)} alt={t.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                    </div>
                    <div className="p-3">
                      <div className="truncate text-sm font-bold text-white">{t.name}</div>
                      <div className="text-[11px] text-[#666]">{t.category} · {t.recommended_aspect}</div>
                      <button onClick={() => chooseTemplate(t)} data-testid={`igv2-use-template-${t.id}`}
                        className="mt-2 w-full rounded-lg bg-[#E1306C] py-1.5 text-xs font-bold text-white hover:bg-[#E1306C]/90">Use Template</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {stage === "studio" && tpl && (
          <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
            <div>
              <button onClick={() => setStage("library")} data-testid="igv2-back-to-library" className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ArrowLeft size={14} /> Back to templates</button>
              <div className="overflow-hidden rounded-xl border border-white/10" data-testid="igv2-selected-template">
                <img src={abs(tpl.image_url)} alt={tpl.name} className="w-full object-cover" />
              </div>
              <div className="mt-2 text-sm font-bold text-white">{tpl.name}</div>
              <div className="text-[11px] text-[#666]">{tpl.source === "user_reference" ? (tpl.origin === "clipboard" ? "Pasted from clipboard" : "Your uploaded reference") : tpl.category}</div>
            </div>

            <div className="space-y-5">
              <h2 className="font-display text-xl font-black text-white">Customize this post</h2>

              <div className="grid grid-cols-2 gap-3">
                <ImageField label="Main Image" testid="main" role="main" value={main} onChange={setMain} />
                <ImageField label="Logo" testid="logo" role="logo" value={logo} onChange={setLogo} />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#A1A1AA]">Reference Images (optional)</label>
                  <div className="flex gap-2">
                    <button onClick={pasteRef} data-testid="igv2-paste-ref" className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-bold text-white hover:border-[#8B5CF6]/60">Paste</button>
                    {refs.length > 0 && <button onClick={() => setRefs([])} data-testid="igv2-clear-refs" className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] font-bold text-[#A1A1AA] hover:text-red-400">Clear all</button>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="igv2-refs">
                  {refs.map((r, i) => (
                    <div key={i} className="relative">
                      <img src={abs(r.url)} alt="ref" className="h-16 w-16 rounded-lg border border-white/15 object-cover" />
                      <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} data-testid={`igv2-remove-ref-${i}`} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-0.5 text-white"><X size={11} /></button>
                    </div>
                  ))}
                  {refs.length < 4 && (
                    <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-white/20 text-[#A1A1AA] hover:border-[#8B5CF6]/60">
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="igv2-add-ref" onChange={(e) => { addRef(e.target.files?.[0]); e.target.value = ""; }} />
                      <ImagePlus size={16} />
                    </label>
                  )}
                </div>
              </div>

              <AssistField label="Headline (optional)" testid="headline" value={headline} onChange={setHeadline} placeholder="e.g. INDIA WIN THE SERIES" />
              <AssistField label="Text / Information" testid="text" value={text} onChange={setText} textarea ocr placeholder="Enter your post text (any language — Telugu / Hindi / English…)" />

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#A1A1AA]">Aspect Ratio</label>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="igv2-aspect-select">
                  {aspects.map((a) => (
                    <button key={a.key} onClick={() => setAspect(a.key)} data-testid={`igv2-aspect-${a.key}`}
                      className={`rounded-lg border px-2 py-2 text-center ${aspect === a.key ? "border-[#E1306C] bg-[#E1306C]/10 text-white" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
                      <div className="text-sm font-bold">{a.key}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="igv2-prompt-section">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles size={14} className="text-[#a78bfa]" /> AI Generation Instructions</div>
                  <div className="flex items-center gap-2">
                    {edited && <span className="rounded-full bg-[#8B5CF6]/25 px-2 py-0.5 text-[10px] font-bold text-[#c4b5fd]" data-testid="igv2-prompt-customized">Prompt customized</span>}
                    <button onClick={() => { setPromptText(genPrompt); setEdited(false); }} data-testid="igv2-reset-prompt" className="rounded-lg border border-white/15 px-2 py-1 text-[10px] font-bold text-[#A1A1AA] hover:text-white">Reset to AI Suggested</button>
                  </div>
                </div>
                <textarea value={promptText} onChange={(e) => { setPromptText(e.target.value); setEdited(true); }} data-testid="igv2-prompt-textarea" rows={7}
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs leading-relaxed text-white outline-none focus:border-[#8B5CF6]/60" />
                <div className="mt-2">
                  <label className="mb-1 block text-[11px] font-semibold text-[#A1A1AA]">Additional user direction (appended)</label>
                  <input value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="igv2-extra-instructions" placeholder="e.g. make the player larger, darker background"
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#8B5CF6]/60" />
                </div>
              </div>

              <button onClick={generate} data-testid="igv2-create-post-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#E1306C] to-[#F77737] py-3.5 text-sm font-black text-white hover:opacity-90">
                <Sparkles size={17} /> Generate Image
              </button>
            </div>
          </div>
        )}

        {stage === "generating" && (
          <div className="flex flex-col items-center py-24" data-testid="igv2-generating">
            <Loader2 size={40} className="animate-spin text-[#E1306C]" />
            <h2 className="mt-6 font-display text-xl font-black text-white">Creating your Instagram post…</h2>
            <div className="mt-6 w-full max-w-sm space-y-2">
              {PROGRESS_STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-2 text-sm">
                  {i < progress ? <Check size={16} className="text-[#22c55e]" /> : i === progress ? <Loader2 size={16} className="animate-spin text-[#E1306C]" /> : <div className="h-4 w-4 rounded-full border border-white/20" />}
                  <span className={i <= progress ? "text-white" : "text-[#555]"}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {stage === "result" && result && (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="flex flex-col items-center">
              <div className="overflow-hidden rounded-xl border border-white/10 bg-black" data-testid="igv2-result-preview" style={{ maxWidth: 520 }}>
                <img src={abs(result.url)} alt="Generated result" className="w-full object-contain" data-testid="igv2-result-image" />
              </div>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <button onClick={() => download(result.url, result.generation_id)} data-testid="igv2-download-btn" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white hover:bg-[#E1306C]/90"><Download size={15} /> Download</button>
                <button onClick={() => { setStage("studio"); setTimeout(generate, 50); }} data-testid="igv2-regenerate-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/30"><RefreshCw size={15} /> Regenerate</button>
                <button onClick={() => setStage("studio")} data-testid="igv2-back-to-edit-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-white/30"><ArrowLeft size={15} /> Edit Inputs</button>
                <button onClick={startAgain} data-testid="igv2-start-again-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-[#A1A1AA] hover:text-white"><RotateCcw size={15} /> Start Again</button>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-bold text-white">This session</h3>
              <div className="space-y-2" data-testid="igv2-history">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-2">
                    <img src={abs(h.url)} alt="" className="h-12 w-12 rounded object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-white">{h.template_name}</div>
                      <div className="text-[10px] text-[#666]">{h.aspect} · {new Date(h.at).toLocaleTimeString()}</div>
                    </div>
                    <button onClick={() => download(h.url, h.generation_id)} className="rounded p-1 text-[#A1A1AA] hover:text-white"><Download size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
